// src/pages/Admin/VotePage/hooks/useVoteForm.js

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import dayjs from "dayjs";
import { toast } from "react-toastify";

import {
  createVote,
  updateVote,
} from "../../../../services/voteServices";

import {
  getVoteAuthDebug,
  logVoteAuthDebug,
} from "../utils/voteAuthDebug";

import { DEFAULT_VOTE_FORM } from "../data/voteAdminOptions";

import {
  buildVotePayload,
  parseBoolean,
  parseIdArray,
  toDateInputValue,
} from "../utils/voteAdmin.utils";

const createFormFromVote = (vote) => {
  if (!vote) {
    return {
      ...DEFAULT_VOTE_FORM,
    };
  }

  const useClubWeight =
    parseBoolean(
      vote.use_club_weight,
      false,
    );

  const oneVotePerClub =
    useClubWeight ||
    parseBoolean(
      vote.one_vote_per_club,
      false,
    );

  return {
    title:
      vote.title || "",

    description:
      vote.description || "",

    start_time:
      toDateInputValue(
        vote.start_time,
      ),

    end_time:
      toDateInputValue(
        vote.end_time,
      ),

    type:
      vote.type || "vote",

    is_active:
      parseBoolean(
        vote.is_active,
        true,
      ),

    is_anonymous:
      parseBoolean(
        vote.is_anonymous,
        true,
      ),

    one_vote_per_club:
      oneVotePerClub,

    use_club_weight:
      useClubWeight,
  };
};

export default function useVoteForm({
  isOpen,
  vote,
  onSaved,
  onClose,
  authDebug,
  userContext,
}) {
  const [
    formData,
    setFormData,
  ] = useState({
    ...DEFAULT_VOTE_FORM,
  });

  const [
    roleIds,
    setRoleIds,
  ] = useState([]);

  const [
    actorGroupIds,
    setActorGroupIds,
  ] = useState([]);

  const [saving, setSaving] =
    useState(false);

  const isEditing =
    Boolean(vote?.id);

  /*
   * Le backend interdit déjà la modification des règles
   * après la première participation.
   *
   * On bloque aussi les boutons côté interface.
   */
  const clubRulesLocked =
    useMemo(
      () =>
        isEditing &&
        Number(
          vote?.voterCount || 0,
        ) > 0,
      [
        isEditing,
        vote?.voterCount,
      ],
    );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData(
      createFormFromVote(vote),
    );

    setRoleIds(
      parseIdArray(
        vote?.allowed_roles,
      ),
    );

    setActorGroupIds(
      parseIdArray(
        vote?.allowed_actor_group_ids ??
          vote?.allowed_group_ids ??
          vote?.actor_group_ids,
      ),
    );
  }, [
    isOpen,
    vote,
  ]);

  const handleFieldChange = (
    event,
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    const nextValue =
      type === "checkbox"
        ? checked
        : value;

    setFormData(
      (currentForm) => ({
        ...currentForm,
        [name]:
          nextValue,
      }),
    );
  };

  const setFieldValue = (
    name,
    value,
  ) => {
    setFormData(
      (currentForm) => ({
        ...currentForm,
        [name]: value,
      }),
    );
  };

  const setOneVotePerClub = (
    enabled,
  ) => {
    if (clubRulesLocked) {
      return;
    }

    setFormData(
      (currentForm) => {
        /*
         * Impossible de désactiver le vote unique
         * tant que la pondération est active.
         */
        if (
          !enabled &&
          currentForm.use_club_weight
        ) {
          return currentForm;
        }

        return {
          ...currentForm,
          one_vote_per_club:
            Boolean(enabled),
        };
      },
    );
  };

  const setClubWeightEnabled = (
    enabled,
  ) => {
    if (clubRulesLocked) {
      return;
    }

    setFormData(
      (currentForm) => ({
        ...currentForm,

        use_club_weight:
          Boolean(enabled),

        /*
         * Activer la pondération active automatiquement
         * le vote unique par club.
         *
         * Désactiver la pondération conserve l'état actuel
         * du vote unique, qui pourra ensuite être décoché.
         */
        one_vote_per_club:
          enabled
            ? true
            : currentForm.one_vote_per_club,
      }),
    );
  };

  const toggleRole = (
    roleId,
    checked,
  ) => {
    const normalizedId =
      Number(roleId);

    setRoleIds(
      (currentIds) => {
        if (checked) {
          return currentIds.includes(
            normalizedId,
          )
            ? currentIds
            : [
                ...currentIds,
                normalizedId,
              ];
        }

        return currentIds.filter(
          (currentId) =>
            currentId !==
            normalizedId,
        );
      },
    );
  };

  const toggleActorGroup = (
    groupId,
    checked,
  ) => {
    const normalizedId =
      Number(groupId);

    setActorGroupIds(
      (currentIds) => {
        if (checked) {
          return currentIds.includes(
            normalizedId,
          )
            ? currentIds
            : [
                ...currentIds,
                normalizedId,
              ];
        }

        return currentIds.filter(
          (currentId) =>
            currentId !==
            normalizedId,
        );
      },
    );
  };

  const clearAudience = () => {
    setRoleIds([]);
    setActorGroupIds([]);
  };

  const validate = () => {
    if (
      !formData.title.trim()
    ) {
      toast.warn(
        "Le titre du vote est obligatoire.",
      );

      return false;
    }

    if (
      !formData.start_time ||
      !formData.end_time
    ) {
      toast.warn(
        "Les dates de début et de fin sont obligatoires.",
      );

      return false;
    }

    const startDate =
      dayjs(
        formData.start_time,
      );

    const endDate =
      dayjs(
        formData.end_time,
      );

    if (
      !startDate.isValid() ||
      !endDate.isValid()
    ) {
      toast.warn(
        "Les dates renseignées ne sont pas valides.",
      );

      return false;
    }

    if (
      endDate.isBefore(
        startDate,
        "day",
      )
    ) {
      toast.warn(
        "La date de fin doit être postérieure ou égale à la date de début.",
      );

      return false;
    }

    if (
      formData.use_club_weight &&
      !formData.one_vote_per_club
    ) {
      toast.warn(
        "La pondération nécessite l’activation du vote unique par club.",
      );

      return false;
    }

    return true;
  };

  const submitForm = async (
    event,
  ) => {
    event?.preventDefault?.();

    if (!validate()) {
      return;
    }

    const currentAuth =
      getVoteAuthDebug(
        userContext,
      );

    logVoteAuthDebug(
      "Soumission du formulaire de vote",
      userContext,
    );

    console.group(
      "[VOTE FORM] Autorisation de création",
    );

    console.log(
      "Utilisateur transmis depuis VotePage :",
      authDebug,
    );

    console.log(
      "Utilisateur recalculé au submit :",
      currentAuth,
    );

    console.log(
      "Utilisateur ID :",
      currentAuth.resolvedUserId,
    );

    console.log(
      "Rôle ID :",
      currentAuth.resolvedRoleId,
    );

    console.log(
      "Est administrateur :",
      currentAuth.isAdmin,
    );

    console.groupEnd();

    if (!currentAuth.isAdmin) {
      console.warn(
        "[VOTE FORM] Le front ne reconnaît pas cet utilisateur comme administrateur.",
      );

      toast.warning(
        `Diagnostic : utilisateur ${
          currentAuth.resolvedUserId ??
          "inconnu"
        }, rôle ${
          currentAuth.resolvedRoleId ??
          "inconnu"
        }.`,
      );
    }

    setSaving(true);

    try {
      const payload =
        buildVotePayload({
          formData,
          roleIds,
          actorGroupIds,
        });

      console.log(
        "[VOTE FORM] Payload envoyé :",
        payload,
      );

      if (isEditing) {
        await updateVote(
          vote.id,
          payload,
        );

        toast.success(
          "Vote modifié avec succès.",
        );
      } else {
        await createVote(
          payload,
        );

        toast.success(
          "Vote créé avec succès.",
        );
      }

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error(
        "Erreur lors de l’enregistrement du vote :",
        error,
      );

      const serverMessage =
        error.response?.data
          ?.message;

      toast.error(
        serverMessage ||
          (isEditing
            ? "Le vote n’a pas pu être modifié."
            : "Le vote n’a pas pu être créé."),
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    formData,
    roleIds,
    actorGroupIds,

    saving,
    isEditing,
    clubRulesLocked,

    handleFieldChange,
    setFieldValue,

    setOneVotePerClub,
    setClubWeightEnabled,

    toggleRole,
    toggleActorGroup,
    clearAudience,

    setRoleIds,
    setActorGroupIds,

    submitForm,
  };
}